import Vue from 'vue'
import i18n from '@/plugins/i18n.plugin'
import api from '@/api'
import UUIDService from '@/helpers/UUIDService'
import {
  defineSyncSchema,
  schemaKinds,
  createSyncState,
  recordWrite,
  createSyncRequest,
  mergeSyncResponse,
  syncMutations
} from '@/helpers/storeSync'

const INFRAESTRUCTURA_SYNC_SCHEMA = defineSyncSchema({ key: 'ID_INFRAESTRUCTURA', kind: 'infraestructura', insertAt: 'end' })

const state = {
  infraestructuras: [],
  tipos: [],
  categorias: [],
  municipios: [],
  provincias: [],
  responsables: [],
  sync: createSyncState(schemaKinds(INFRAESTRUCTURA_SYNC_SCHEMA))
}

const getters = {
  infraestructuras: (state) => {
    return state.infraestructuras
  },
  tipos: (state) => {
    return state.tipos
  },
  categorias: (state) => {
    return state.categorias
  },
  municipios: (state) => {
    return state.municipios
  },
  getMunicipioByName: (state) => (nombre) => {
    return state.municipios.find(municipio => municipio.NOMBRE === nombre)
  },

  provincias: (state) => {
    return state.provincias
  },
  responsables: (state) => {
    return state.responsables
  }
}

const mutations = {
  ...syncMutations,
  MERGE_INFRAESTRUCTURAS(state, { rows, request }) {
    state.infraestructuras = mergeSyncResponse(state.sync, state.infraestructuras, rows, INFRAESTRUCTURA_SYNC_SCHEMA, request)
  },
  SOCKET_addInfraestructura(state, infraestructura) {
    state.infraestructuras.push(infraestructura)
    recordWrite(state.sync, 'infraestructura', infraestructura.ID_INFRAESTRUCTURA)
    this.notify(i18n.t('alert.infrastructureCreatedTitle'), i18n.t('alert.infrastructureCreated', { m: infraestructura.NOMBRE }))
  },
  SOCKET_updateInfraestructura(state, data) {
    const positionInfraestructura = state.infraestructuras.findIndex(x => x.ID_INFRAESTRUCTURA === data.ID_INFRAESTRUCTURA)

    if (positionInfraestructura !== -1) {
      Vue.set(state.infraestructuras, positionInfraestructura, data)
      recordWrite(state.sync, 'infraestructura', data.ID_INFRAESTRUCTURA)
      this.notify(i18n.t('alert.infrastructureUpdatedTitle'), i18n.t('alert.infrastructureUpdated', { m: data.NOMBRE }))
    }
  },

  SET_TIPOS(state, tipos) {
    state.tipos = tipos
  },

  SET_CATEGORIAS(state, categorias) {
    state.categorias = categorias
  },

  SET_MUNICIPIOS(state, municipios) {
    state.municipios = municipios
  },

  SET_PROVINCIAS(state, provincias) {
    state.provincias = provincias
  },

  SET_RESPONSABLES(state, responsables) {
    state.responsables = responsables
  }
}

const actions = {
  async fetchInfraestructuras({ commit, state }) {

    try {
      commit('BEGIN_SYNC_REQUEST')
      const request = createSyncRequest(state.sync.seq)
      const response = await api.infraestructura.getInfraestructuras()
      commit('MERGE_INFRAESTRUCTURAS', { rows: response.data, request })

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async addInfraestructura({ commit }, data) {

    try {
      data.ID_INFRAESTRUCTURA = UUIDService.createUUID()
      const response = await api.infraestructura.addInfraestructura(data)
      if (response.status === 200) {
        commit('SOCKET_addInfraestructura', data)
      }

    } catch (err) {
      console.error(err)
      this.$notify(i18n.t('alert.error.error'), i18n.t('alert.error.infrastructureCreation'), i18n.t('alert.error.error'))
    }

  },
  async updateInfraestructura({ commit }, data) {

    try {
      const response = await api.infraestructura.updateInfraestructura(data)
      if (response.status === 200) {
        commit('SOCKET_updateInfraestructura', data)
      }

    } catch (err) {
      console.error(err)
      this.$notify(i18n.t('alert.error.error'), i18n.t('alert.error.infrastructureUpdate'), i18n.t('alert.error.error'))
    }

  },
  async fetchTipos({ commit }) {

    try {
      const response = await api.infraestructura.getTipos()
      commit('SET_TIPOS', response.data)

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async fetchCategorias({ commit }) {

    try {
      const response = await api.infraestructura.getCategorias()
      commit('SET_CATEGORIAS', response.data)

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async fetchProvincias({ commit }) {

    try {
      const response = await api.infraestructura.getProvincias()
      commit('SET_PROVINCIAS', response.data)

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async fetchMunicipios({ commit }) {

    try {
      const response = await api.infraestructura.getMunicipios()
      commit('SET_MUNICIPIOS', response.data)

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async fetchOrganismos({ commit }) {

    try {
      const response = await api.infraestructura.getResponsables()
      commit('SET_RESPONSABLES', response.data)

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async disableInfraestructura({ commit }, data) {

    try {
      const dataCopy = JSON.parse(JSON.stringify(data))
      dataCopy.BORRADO = true
      const response = await api.infraestructura.updateInfraestructura(dataCopy)
      if (response.status === 200) {
        commit('SOCKET_updateInfraestructura', dataCopy)
      }

    } catch (err) {
      console.error(err)
      throw err
    }

  },
  async enableInfraestructura({ commit }, data) {

    try {
      const dataCopy = JSON.parse(JSON.stringify(data))
      dataCopy.BORRADO = false
      const response = await api.infraestructura.updateInfraestructura(dataCopy)
      if (response.status === 200) {
        commit('SOCKET_updateInfraestructura', dataCopy)
      }

    } catch (err) {
      console.error(err)
      throw err
    }

  }
}

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default module
